import { useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import { useQueryParams } from '../useQueryParams';
import { useLazyGetConversationByIdQuery } from 'src/store/services';
import { convertSlugToConversationId, getErrorStatus } from 'src/utils';
import { useGetUser } from '../userHooks';
import { AppRoutes, isMessageWithCreationTaskId, Message } from 'src/types';
import { TEMPORARY_EMPTY_MESSAGE } from 'src/constants';

export const useLoadDefaultMessageList = () => {
  const navigate = useNavigate();

  const { user_id } = useGetUser();

  const { searchParams, removeSearchParam } = useQueryParams();
  const {
    task,
    conv,
    first_comparing_conv,
    second_comparing_conv,
    active_compare_list,
  } = searchParams;

  const { mainMessageListRef, compareMessageListRef } =
    useContext(ForwardRefContext);
  const [trigger] = useLazyGetConversationByIdQuery();

  useEffect(() => {
    const getMessagesToReplace = (messages: Message[]) => {
      if (messages.length > 1) {
        return messages;
      }

      return [...messages, TEMPORARY_EMPTY_MESSAGE];
    };

    const loadMessages = async () => {
      if (task) {
        removeSearchParam('task');
      }

      const mainId = convertSlugToConversationId(conv || first_comparing_conv);
      const compareId = convertSlugToConversationId(second_comparing_conv);

      try {
        const [mainResult, compareResult] = await Promise.all([
          mainId
            ? trigger({ conversation_id: mainId || '', user_id }, true).unwrap()
            : Promise.resolve(undefined),
          compareId
            ? trigger({ conversation_id: compareId, user_id }, true).unwrap()
            : Promise.resolve(undefined),
        ]);

        if (!!compareResult?.messages && compareResult.messages.length > 0) {
          const { messages } = compareResult;
          compareMessageListRef?.current?.data.replace(
            getMessagesToReplace(messages),
            {
              initialLocation: {
                index: 'LAST',
                align: 'end',
                behavior: 'instant',
              },
              purgeItemSizes: true,
            },
          );
        }

        if (!!mainResult?.messages && mainResult.messages.length > 0) {
          const { messages } = mainResult;
          mainMessageListRef?.current?.data.replace(
            getMessagesToReplace(messages),
            {
              initialLocation: {
                index: 'LAST',
                align: 'end',
                behavior: 'instant',
              },
              purgeItemSizes: true,
            },
          );

          if (!task) {
            return;
          }

          const currentIndex = messages.findIndex(
            (item) =>
              isMessageWithCreationTaskId(item) &&
              item.payload.task_id === task,
          );

          setTimeout(() => {
            mainMessageListRef?.current?.scrollToItem({
              index: currentIndex - 1,
              align: 'start',
              behavior: 'smooth',
            });
          }, 150);
        }
      } catch (error) {
        if (getErrorStatus(error) === 404) {
          navigate(AppRoutes.NOT_FOUND, { replace: true });
        }
      }
    };

    if (conv || (first_comparing_conv && second_comparing_conv)) {
      loadMessages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv, first_comparing_conv, second_comparing_conv, active_compare_list]);
};
